import { apiGet } from '../lib/apiClient';

// Reviews now come from the backend `/v1/website/reviews` endpoint (Supabase ->
// Cloud SQL migration), NOT the Supabase `google_reviews` / `google_place_*`
// tables. One endpoint returns everything:
//   { reviews: [...], stats: {...}|null, metadata: {...}|null }
// (camelCase; review `time` is a unix-seconds number). The backend wraps the
// payload as { data: ... }; apiGet unwraps `.data` for us.
//
// FALLBACK-FIRST: every function below returns the same safe fallback the
// consumers already expect (empty array / null) on ANY failure. Nothing here
// ever falls back to Supabase — we are removing it.

const REVIEWS_PATH = '/reviews';

// Normalize a single review row into the shape the UI consumes. The backend is
// already camelCase, but map defensively so a snake_case field never breaks a card.
const normalizeReview = (r) => ({
  id: r?.id,
  googleReviewId: r?.googleReviewId ?? r?.google_review_id,
  authorName: r?.authorName ?? r?.author_name,
  authorUrl: r?.authorUrl ?? r?.author_url,
  profilePhotoUrl: r?.profilePhotoUrl ?? r?.profile_photo_url,
  rating: r?.rating,
  text: r?.text,
  time: r?.time, // unix seconds
  relativeTimeDescription: r?.relativeTimeDescription ?? r?.relative_time_description,
  language: r?.language,
  fetchedAt: r?.fetchedAt ?? r?.fetched_at,
  createdAt: r?.createdAt ?? r?.created_at,
});

/**
 * Fetch all Google reviews from the backend, ordered by time descending.
 * Safe fallback: empty array (homepage + reviews page render their empty state).
 */
export const fetchGoogleReviews = async () => {
  try {
    const payload = await apiGet(REVIEWS_PATH);
    const reviews = payload?.reviews || [];
    return reviews
      .map(normalizeReview)
      .sort((a, b) => (b?.time || 0) - (a?.time || 0));
  } catch (err) {
    console.error('Error fetching reviews:', err?.message);
    return [];
  }
};

/**
 * Manual "Sync from Google" trigger.
 *
 * NOTE: the backend `/v1/website/*` surface (MR !71) does NOT expose a reviews
 * sync endpoint — syncing from the Google Places API is a server-side/cron
 * concern now, not something the marketing site triggers. This is therefore a
 * no-op that resolves cleanly so the reviews page's auto-sync + "Sync from
 * Google" button degrade gracefully (loadReviews still re-reads the endpoint).
 */
export const syncGoogleReviews = async () => {
  return null;
};

/**
 * Fetch the Google Place metadata (total rating count from Google).
 * Safe fallback: null.
 */
export const fetchPlaceMetadata = async () => {
  try {
    const payload = await apiGet(REVIEWS_PATH);
    const m = payload?.metadata;
    if (!m) return null;
    return {
      totalRatingCount: m?.totalRatingCount ?? m?.total_rating_count,
      overallRating: m?.overallRating ?? m?.overall_rating,
      lastSyncedAt: m?.lastSyncedAt ?? m?.last_synced_at,
    };
  } catch (err) {
    console.warn('Could not fetch place metadata:', err?.message);
    return null;
  }
};

/**
 * Fetch the place-level stats (total review count + overall rating).
 * Safe fallback: null (consumers fall back to their hardcoded FALLBACK_* values).
 */
export const fetchPlaceStats = async () => {
  try {
    const payload = await apiGet(REVIEWS_PATH);
    const s = payload?.stats;
    if (!s) return null;
    return {
      totalReviews: s?.totalReviews ?? s?.userRatingCount ?? s?.user_rating_count,
      overallRating: s?.overallRating ?? s?.rating,
      updatedAt: s?.updatedAt ?? s?.updated_at,
    };
  } catch (err) {
    console.error('Error fetching place stats:', err?.message);
    return null;
  }
};

/**
 * Calculate aggregate stats from a list of reviews (client-side; unchanged).
 */
export const calculateReviewStats = (reviews) => {
  if (!reviews || reviews?.length === 0) {
    return {
      overallRating: 0,
      totalReviews: 0,
      satisfactionRate: 0,
      ratingDistribution: [5, 4, 3, 2, 1]?.map((stars) => ({ stars, percentage: 0 })),
    };
  }

  const total = reviews?.length;
  const sum = reviews?.reduce((acc, r) => acc + r?.rating, 0);
  const overallRating = Math.round((sum / total) * 10) / 10;

  const fourPlusStar = reviews?.filter((r) => r?.rating >= 4)?.length;
  const satisfactionRate = Math.round((fourPlusStar / total) * 100);

  const ratingDistribution = [5, 4, 3, 2, 1]?.map((stars) => {
    const count = reviews?.filter((r) => r?.rating === stars)?.length;
    return {
      stars,
      percentage: Math.round((count / total) * 100),
    };
  });

  return {
    overallRating,
    totalReviews: total,
    satisfactionRate,
    ratingDistribution,
  };
};
